import { db } from '../firebase';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  deleteDoc,
  updateDoc,
  orderBy,
  query,
  runTransaction,
  serverTimestamp,
} from 'firebase/firestore';
import { localDateKey, localDateKeys } from '../utils/dateKey';

// ── Workouts ──────────────────────────────────────────────────────────────────

export async function fsGetWorkouts(uid) {
  if (!uid || !db) return null;
  const q = query(collection(db, 'users', uid, 'workouts'), orderBy('createdAt', 'desc'));
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

export async function fsSaveWorkout(uid, workout) {
  if (!uid || !db) return null;
  const id = workout.id || String(Date.now());
  const entry = { ...workout, id, createdAt: workout.createdAt || new Date().toISOString() };
  await setDoc(doc(db, 'users', uid, 'workouts', id), entry);
  return entry;
}

export async function fsDeleteWorkout(uid, id) {
  if (!uid || !db) return;
  await deleteDoc(doc(db, 'users', uid, 'workouts', id));
}

// ── Gym Schedule ──────────────────────────────────────────────────────────────

export async function fsGetSchedule(uid) {
  if (!uid || !db) return null;
  const snap = await getDocs(collection(db, 'users', uid, 'schedule'));
  if (snap.empty) return null;
  const schedule = {};
  snap.docs.forEach((d) => {
    schedule[d.id] = d.data();
  });
  return schedule;
}

export async function fsSaveSchedule(uid, schedule) {
  if (!uid || !db) return;
  const writes = Object.entries(schedule).map(([day, data]) =>
    setDoc(doc(db, 'users', uid, 'schedule', day), data)
  );
  await Promise.all(writes);
}

// ── Completions ───────────────────────────────────────────────────────────────

/**
 * Toggle a single exercise for the given dayKey on today's date.
 * Returns the updated array of completed exercises for that day.
 */
export async function fsToggleExercise(uid, dayKey, exercise) {
  if (!uid || !db) return null;
  const today = new Date().toISOString().split('T')[0];
  const ref = doc(db, 'users', uid, 'completions', today);
  const snap = await getDoc(ref);
  const data = snap.exists() ? snap.data() : {};
  const current = data[dayKey] || [];
  const updated = current.includes(exercise)
    ? current.filter((e) => e !== exercise)
    : [...current, exercise];
  await setDoc(ref, { ...data, [dayKey]: updated }, { merge: true });
  return updated;
}

export async function fsGetTodayCompletions(uid, dayKey) {
  if (!uid || !db) return null;
  const today = new Date().toISOString().split('T')[0];
  const snap = await getDoc(doc(db, 'users', uid, 'completions', today));
  if (!snap.exists()) return [];
  return snap.data()[dayKey] || [];
}

// ── Steps ─────────────────────────────────────────────────────────────────────

export async function fsGetTodaySteps(uid) {
  if (!uid || !db) return null;
  const today = new Date().toISOString().split('T')[0];
  const snap = await getDoc(doc(db, 'users', uid, 'steps', today));
  return snap.exists() ? (snap.data().count ?? 0) : 0;
}

export async function fsLogSteps(uid, count) {
  if (!uid || !db) return;
  const today = new Date().toISOString().split('T')[0];
  await setDoc(doc(db, 'users', uid, 'steps', today), { count });
}

export async function fsGetWeekSteps(uid) {
  if (!uid || !db) return null;
  const now = new Date();
  const dates = Array.from({ length: 7 }, (_, i) => {
    const d = new Date(now);
    d.setDate(d.getDate() - (6 - i));
    return {
      date: d.toISOString().split('T')[0],
      day: d.toLocaleDateString('en-GB', { weekday: 'short' }),
    };
  });

  const snaps = await Promise.all(
    dates.map(({ date }) => getDoc(doc(db, 'users', uid, 'steps', date)))
  );

  return dates.map(({ date, day }, i) => ({
    date,
    day,
    steps: snaps[i].exists() ? (snaps[i].data().count ?? 0) : 0,
  }));
}

export async function fsGetStepGoal(uid) {
  if (!uid || !db) return null;
  const snap = await getDoc(doc(db, 'users', uid, 'preferences'));
  if (!snap.exists()) return null;
  const goal = snap.data().stepGoal;
  return goal != null ? goal : null;
}

export async function fsSaveStepGoal(uid, goal) {
  if (!uid || !db) return;
  await setDoc(doc(db, 'users', uid, 'preferences'), { stepGoal: goal }, { merge: true });
}

// ── Sport Sessions ─────────────────────────────────────────────────────────────

export async function fsGetUpcomingSessions(uid) {
  if (!uid || !db) return null;
  const today = new Date().toISOString().split('T')[0];
  const snap = await getDocs(collection(db, 'users', uid, 'sport_sessions'));
  return snap.docs
    .map((d) => ({ id: d.id, ...d.data() }))
    .filter((s) => s.date >= today)
    .sort((a, b) => a.date.localeCompare(b.date));
}

export async function fsGetAllSportSessions(uid) {
  if (!uid || !db) return null;
  const snap = await getDocs(collection(db, 'users', uid, 'sport_sessions'));
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

export async function fsSaveSportSession(uid, session) {
  if (!uid || !db) return null;
  const id = session.id || String(Date.now());
  const entry = { ...session, id, createdAt: session.createdAt || new Date().toISOString() };
  await setDoc(doc(db, 'users', uid, 'sport_sessions', id), entry);
  return entry;
}

export async function fsDeleteSportSession(uid, id) {
  if (!uid || !db) return;
  await deleteDoc(doc(db, 'users', uid, 'sport_sessions', id));
}

export async function fsToggleSportSession(uid, id) {
  if (!uid || !db) return;
  const ref = doc(db, 'users', uid, 'sport_sessions', id);
  const snap = await getDoc(ref);
  if (snap.exists()) {
    await updateDoc(ref, { completed: !snap.data().completed });
  }
}

// ── Custom Sports ────────────────────────────────────────────────────────────

export async function fsGetCustomSports(uid) {
  if (!uid || !db) return null;
  const snap = await getDocs(collection(db, 'users', uid, 'custom_sports'));
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

export async function fsSaveCustomSport(uid, sport) {
  if (!uid || !db) return null;
  const id = sport.id || `custom_${Date.now()}`;
  const entry = { ...sport, id, createdAt: sport.createdAt || new Date().toISOString() };
  await setDoc(doc(db, 'users', uid, 'custom_sports', id), entry);
  return entry;
}

export async function fsDeleteCustomSport(uid, id) {
  if (!uid || !db) return;
  await deleteDoc(doc(db, 'users', uid, 'custom_sports', id));
}

// ── Water Intake ──────────────────────────────────────────────────────────────

export async function fsGetWater(uid) {
  if (!uid || !db) return null;
  const today = localDateKey();
  const [dailySnap, prefsSnap] = await Promise.all([
    getDoc(doc(db, 'users', uid, 'water', today)),
    getDoc(doc(db, 'users', uid, 'preferences', 'health')),
  ]);
  if (!dailySnap.exists() && !prefsSnap.exists()) return null;
  const daily = dailySnap.exists() ? dailySnap.data() : {};
  const prefs = prefsSnap.exists() ? prefsSnap.data() : {};
  const amountMl = Number.isFinite(daily.amountMl)
    ? Math.max(0, Math.round(daily.amountMl))
    : Math.max(0, Math.round((daily.glasses || 0) * 250));
  const goalMl = Number.isFinite(prefs.waterGoalMl)
    ? Math.max(250, Math.round(prefs.waterGoalMl))
    : Number.isFinite(daily.goalMl)
      ? Math.max(250, Math.round(daily.goalMl))
      : Math.max(250, Math.round((daily.goal || prefs.waterGoal || 8) * 250));
  return {
    ...daily,
    hasDaily: dailySnap.exists(),
    hasGoal: Number.isFinite(prefs.waterGoalMl) ||
      Number.isFinite(daily.goalMl) || Number.isFinite(daily.goal) ||
      Number.isFinite(prefs.waterGoal),
    amountMl,
    glasses: amountMl / 250,
    goalMl,
    goal: goalMl / 250,
  };
}

export async function fsLogWater(uid, amountMl) {
  if (!uid || !db) return;
  const today = localDateKey();
  const safeAmount = Math.min(100000, Math.max(0, Math.round(amountMl)));
  await setDoc(doc(db, 'users', uid, 'water', today), {
    amountMl: safeAmount,
    glasses: safeAmount / 250,
    timezoneOffsetMinutes: -new Date().getTimezoneOffset(),
    updatedAt: serverTimestamp(),
  }, { merge: true });
}

export async function fsSetWaterGoal(uid, goalMl) {
  if (!uid || !db) return;
  const safeGoal = Math.min(20000, Math.max(250, Math.round(goalMl)));
  const today = localDateKey();
  await Promise.all([
    setDoc(doc(db, 'users', uid, 'preferences', 'health'), {
      waterGoalMl: safeGoal,
      waterGoal: safeGoal / 250,
      updatedAt: serverTimestamp(),
    }, { merge: true }),
    setDoc(doc(db, 'users', uid, 'water', today), {
      goalMl: safeGoal,
      goal: safeGoal / 250,
    }, { merge: true }),
  ]);
}

export async function fsGetWeekWater(uid) {
  if (!uid || !db) return null;
  const dates = localDateKeys(7).map((date) => ({
    date,
    day: new Date(`${date}T12:00:00`).toLocaleDateString('en-GB', { weekday: 'short' }),
  }));
  const snaps = await Promise.all(
    dates.map(({ date }) => getDoc(doc(db, 'users', uid, 'water', date)))
  );
  return dates.map(({ date, day }, i) => {
    const data = snaps[i].exists() ? snaps[i].data() : {};
    const amountMl = Number.isFinite(data.amountMl)
      ? Math.max(0, Math.round(data.amountMl))
      : Math.max(0, Math.round((data.glasses || 0) * 250));
    return { date, day, amountMl, glasses: amountMl / 250 };
  });
}

// ── Weight tracking ───────────────────────────────────────────────────────────

export async function fsGetWeight(uid) {
  if (!uid || !db) return null;
  const today = new Date().toISOString().split('T')[0];
  const snap = await getDoc(doc(db, 'users', uid, 'weight', today));
  return snap.exists() ? snap.data() : null;
}

export async function fsLogWeight(uid, weight, unit) {
  if (!uid || !db) return;
  const today = new Date().toISOString().split('T')[0];
  await setDoc(doc(db, 'users', uid, 'weight', today), { weight, unit });
}

export async function fsGetWeekWeight(uid) {
  if (!uid || !db) return null;
  const now = new Date();
  const dates = Array.from({ length: 7 }, (_, i) => {
    const d = new Date(now);
    d.setDate(d.getDate() - (6 - i));
    return {
      date: d.toISOString().split('T')[0],
      day: d.toLocaleDateString('en-GB', { weekday: 'short' }),
    };
  });
  const snaps = await Promise.all(
    dates.map(({ date }) => getDoc(doc(db, 'users', uid, 'weight', date)))
  );
  return dates.map(({ date, day }, i) => ({
    date,
    day,
    weight: snaps[i].exists() ? (snaps[i].data().weight ?? null) : null,
  }));
}

export async function fsGetWeightPrefs(uid) {
  if (!uid || !db) return null;
  const snap = await getDoc(doc(db, 'users', uid, 'preferences'));
  if (!snap.exists()) return null;
  const d = snap.data();
  if (d.height == null && d.weightUnit == null) return null;
  return { height: d.height ?? null, weightUnit: d.weightUnit ?? null };
}

export async function fsSaveWeightPrefs(uid, prefs) {
  if (!uid || !db) return;
  await setDoc(doc(db, 'users', uid, 'preferences'), prefs, { merge: true });
}

// ── Calorie Counter ───────────────────────────────────────────────────────────

export async function fsGetCalories(uid) {
  if (!uid || !db) return null;
  const today = localDateKey();
  const [dailySnap, prefsSnap] = await Promise.all([
    getDoc(doc(db, 'users', uid, 'calories', today)),
    getDoc(doc(db, 'users', uid, 'preferences', 'health')),
  ]);
  if (!dailySnap.exists() && !prefsSnap.exists()) return null;
  const daily = dailySnap.exists() ? dailySnap.data() : {};
  const preferences = prefsSnap.exists() ? prefsSnap.data() : {};
  return {
    ...daily,
    hasDaily: dailySnap.exists(),
    hasGoal: Number.isFinite(preferences.calorieGoal) ||
      Number.isFinite(daily.goal),
    entries: Array.isArray(daily.entries) ? daily.entries : [],
    goal: Number.isFinite(preferences.calorieGoal)
      ? preferences.calorieGoal
      : (daily.goal ?? 2000),
  };
}

export async function fsLogCalorieEntry(uid, entry) {
  if (!uid || !db) return;
  const today = localDateKey();
  const ref = doc(db, 'users', uid, 'calories', today);
  await runTransaction(db, async (transaction) => {
    const snap = await transaction.get(ref);
    const existing = snap.exists() && Array.isArray(snap.data().entries)
      ? snap.data().entries
      : [];
    if (existing.some((item) => item.id === entry.id)) return;
    transaction.set(ref, {
      entries: [...existing, entry],
      date: today,
      updatedAt: serverTimestamp(),
    }, { merge: true });
  });
}

export async function fsDeleteCalorieEntry(uid, entryId) {
  if (!uid || !db) return;
  const today = localDateKey();
  const ref = doc(db, 'users', uid, 'calories', today);
  await runTransaction(db, async (transaction) => {
    const snap = await transaction.get(ref);
    if (!snap.exists()) return;
    const entries = (snap.data().entries || []).filter((e) => e.id !== entryId);
    transaction.set(ref, { entries, updatedAt: serverTimestamp() }, { merge: true });
  });
}

export async function fsSetCalorieGoal(uid, goal) {
  if (!uid || !db) return;
  const today = localDateKey();
  const safeGoal = Math.min(20000, Math.max(1, Math.round(goal)));
  await Promise.all([
    setDoc(doc(db, 'users', uid, 'preferences', 'health'), {
      calorieGoal: safeGoal,
      updatedAt: serverTimestamp(),
    }, { merge: true }),
    setDoc(doc(db, 'users', uid, 'calories', today), { goal: safeGoal }, { merge: true }),
  ]);
}

// ── Meal planning ─────────────────────────────────────────────────────────────

export async function fsGetMealPlan(uid) {
  if (!uid || !db) return null;
  const snap = await getDoc(doc(db, 'users', uid, 'meal_plans', 'current'));
  if (!snap.exists()) return null;
  const plan = snap.data().plan;
  return plan && typeof plan === 'object' ? plan : {};
}

export async function fsSaveMealPlan(uid, plan) {
  if (!uid || !db) return;
  await setDoc(doc(db, 'users', uid, 'meal_plans', 'current'), {
    plan,
    updatedAt: serverTimestamp(),
  }, { merge: true });
}
